import type { CSSProperties, PointerEventHandler, RefObject } from 'react'
import { useRef, useState } from 'react'
import { useLocalStorage, useWindowSize } from 'usehooks-ts'

export type DraggableButtonCorner =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'

type Position = { x: number; y: number }
type Size = { width: number; height: number }

type UseDraggableButtonOptions = {
  defaultCorner?: DraggableButtonCorner
  dragThreshold: number
  margin: number
  onClick: () => void
  storageKey: string
}

type UseDraggableButtonResult = {
  buttonRef: RefObject<HTMLButtonElement | null>
  buttonStyle: CSSProperties
  containerStyle: CSSProperties
  handlePointerCancel: PointerEventHandler<HTMLButtonElement>
  handlePointerDown: PointerEventHandler<HTMLButtonElement>
  handlePointerMove: PointerEventHandler<HTMLButtonElement>
  handlePointerUp: PointerEventHandler<HTMLButtonElement>
}

const FALLBACK_SIZE = 56

const CORNERS: DraggableButtonCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]

const isCorner = (value: unknown): value is DraggableButtonCorner =>
  typeof value === 'string' && CORNERS.includes(value as DraggableButtonCorner)

const getButtonSize = (button: HTMLButtonElement | null) => {
  const rect = button?.getBoundingClientRect()
  return {
    width: Math.round(rect?.width ?? FALLBACK_SIZE) || FALLBACK_SIZE,
    height: Math.round(rect?.height ?? FALLBACK_SIZE) || FALLBACK_SIZE,
  }
}

const getCornerPosition = (
  corner: DraggableButtonCorner,
  button: HTMLButtonElement | null,
  margin: number,
): Position => {
  const { width, height } = getButtonSize(button)
  const maxX = window.innerWidth - width - margin
  const maxY = window.innerHeight - height - margin

  switch (corner) {
    case 'top-left':
      return { x: margin, y: margin }
    case 'top-right':
      return { x: maxX, y: margin }
    case 'bottom-left':
      return { x: margin, y: maxY }
    case 'bottom-right':
      return { x: maxX, y: maxY }
  }
}

const getNearestCorner = (
  position: Position,
  button: HTMLButtonElement | null,
  margin: number,
): DraggableButtonCorner =>
  CORNERS.reduce((nearest, corner) => {
    const candidate = getCornerPosition(corner, button, margin)
    const nearestPosition = getCornerPosition(nearest, button, margin)

    return Math.hypot(position.x - candidate.x, position.y - candidate.y) <
      Math.hypot(position.x - nearestPosition.x, position.y - nearestPosition.y)
      ? corner
      : nearest
  })

const clampPosition = (
  position: Position,
  buttonSize: Size,
  margin: number,
  windowSize: Size,
): Position => {
  const maxX = windowSize.width - buttonSize.width - margin
  const maxY = windowSize.height - buttonSize.height - margin

  return {
    x: Math.min(Math.max(margin, position.x), maxX),
    y: Math.min(Math.max(margin, position.y), maxY),
  }
}

export const useDraggableButton = ({
  defaultCorner = 'bottom-right',
  dragThreshold,
  margin,
  onClick,
  storageKey,
}: UseDraggableButtonOptions): UseDraggableButtonResult => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dragRef = useRef({
    moved: false,
    offsetX: 0,
    offsetY: 0,
    pointerId: null as number | null,
    startX: 0,
    startY: 0,
  })
  const buttonSize = useRef<Size>(null)

  const windowSize = useWindowSize()
  const [storedCorner, setStoredCorner] =
    useLocalStorage<DraggableButtonCorner>(storageKey, defaultCorner)
  const corner = isCorner(storedCorner) ? storedCorner : defaultCorner

  const [dragPosition, setDragPosition] = useState<Position | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const position =
    dragPosition ?? getCornerPosition(corner, buttonRef.current, margin)

  const handlePointerDown: PointerEventHandler<HTMLButtonElement> = event => {
    dragRef.current = {
      moved: false,
      offsetX: event.clientX - position.x,
      offsetY: event.clientY - position.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    }
    buttonSize.current = getButtonSize(buttonRef.current)
    setDragPosition(position)
    setIsDragging(false)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove: PointerEventHandler<HTMLButtonElement> = event => {
    const drag = dragRef.current
    if (drag.pointerId !== event.pointerId) return

    if (!drag.moved) {
      const distance = Math.hypot(
        event.clientX - drag.startX,
        event.clientY - drag.startY,
      )
      if (distance < dragThreshold) return

      drag.moved = true
      setIsDragging(true)
    }

    setDragPosition(
      clampPosition(
        {
          x: event.clientX - drag.offsetX,
          y: event.clientY - drag.offsetY,
        },
        buttonSize.current,
        margin,
        windowSize,
      ),
    )
  }

  const endDrag = (
    event: React.PointerEvent<HTMLButtonElement>,
    canceled = false,
  ) => {
    const drag = dragRef.current
    if (drag.pointerId !== event.pointerId) return

    const wasDragging = drag.moved
    event.currentTarget.releasePointerCapture(event.pointerId)
    dragRef.current.pointerId = null
    setIsDragging(false)

    if (wasDragging) {
      const finalPosition = clampPosition(
        {
          x: event.clientX - drag.offsetX,
          y: event.clientY - drag.offsetY,
        },
        buttonSize.current,
        margin,
        windowSize,
      )
      setStoredCorner(
        getNearestCorner(finalPosition, buttonRef.current, margin),
      )
    }

    setDragPosition(null)

    if (!wasDragging && !canceled) onClick()
  }

  return {
    buttonRef,
    buttonStyle: {
      cursor: isDragging ? 'grabbing' : 'grab',
      touchAction: 'none',
    },
    containerStyle: {
      left: position.x,
      top: position.y,
      transition: isDragging ? undefined : 'left 160ms ease, top 160ms ease',
    },
    handlePointerCancel: event => endDrag(event, true),
    handlePointerDown,
    handlePointerMove,
    handlePointerUp: endDrag,
  }
}
